import xhrService from '../xhrService';
import { formatISO } from 'date-fns';
import db from '../../db/db';
import { random } from 'lodash';
import { getDateOnly } from '../../utils/date';
import { withOfflineFallback, withOfflineWrite } from '../offlineFallback';
export const baseUrl = '/api/inspections';
const getUrl = (id) => `${baseUrl}/${id}`;

const errors = {
  inspection: 'All required inspection questions must be answered.',
  acknowledgement:
    'All inspector signature(s) are required, before any acknowledgement signature(s) may be submitted.',
  compliance:
    'All inspector signature(s) are required, before any compliance signature(s) may be submitted.',
};

const updateOfflineInspection = async (id, data, online) => {
  const numericId = Number(id);

  // Helper function to fetch data from database
  const fetchFromDb = async (table) => {
    try {
      return await db[table].get(numericId);
    } catch (e) {
      console.log('error', e);
      throw e;
    }
  };

  // Helper function to update database
  const updateDb = async (table, d) => {
    try {
      return await db[table].update(numericId, d);
    } catch (e) {
      console.log('error', e);
      throw e;
    }
  };

  // Fetch all required data in parallel
  const [questions, inspection, certification] = await Promise.all([
    fetchFromDb('questions'),
    fetchFromDb('inspections'),
    fetchFromDb('certifications'),
  ]);

  // Map question IDs to answers
  const answersMap = new Map();

  for (const key in data) {
    const questionId = Number(key.split('-')[1])
      ? Number(key.split('-')[1])
      : Number(key);

    answersMap.set(questionId, data[key]);
  }

  // Process questions and collect unanswered required questions
  const unansweredAndRequired = [];

  let inspection_types = inspection.inspection_types || [];

  let inspectionDate = inspection.created_date;

  questions.template.question_groups.forEach((group) => {
    group.questions.forEach((question) => {
      if (answersMap.has(question.id)) {
        // Update inspection types if this is the type question
        if (question.input_label === 'Inspection Type') {
          const newType = answersMap.get(question.id);
          // If it's not already in the array, add it

          if (
            newType &&
            !inspection_types
              .map((inspection_types) => inspection_types.type)
              .includes(newType)
          ) {
            inspection_types = [...inspection_types, { type: newType }];
          }
        }
        if (question.features.includes('isInspectionDate')) {
          inspectionDate = getDateOnly(new Date(answersMap.get(question.id)));
        }

        // Check if required question is unanswered
        if (
          question.features.includes('required') &&
          answersMap.get(question.id) === ''
        ) {
          unansweredAndRequired.push(question);
        }

        // Update the answer
        question.answer = answersMap.get(question.id);
      }
    });
  });

  // SW2-2446: answers queue as one set, sent whole by the sync. Merge rather than replace, newest
  // answer winning: replacing dropped answers queued by an earlier offline save that this one did
  // not carry, and an online save that left the queue alone had the sync send the older answers
  // over the ones just saved.
  const queuedAnswers =
    questions.sync && questions.sync.synced === false ? questions.sync.payload : null;

  if (!online) {
    questions.updated_at = new Date();
    questions.sync = {
      method: 'PUT',
      payload: { ...(queuedAnswers || {}), ...data },
      synced: false,
    };
  } else if (queuedAnswers) {
    questions.sync = { ...questions.sync, payload: { ...queuedAnswers, ...data } };
  }
  // Determine inspection status
  const status = unansweredAndRequired.length ? 'started' : 'answered';

  // Update questions in database
  await updateDb('questions', questions);

  // Update inspection status and inspection_types
  await updateDb('inspections', {
    created_date: inspectionDate,
    status,
    inspection_types,
  });

  // Update certification if inspection is complete
  if (status === 'answered') {
    certification.groupOptions.inspection.errors = [];
    certification.groupOptions.inspection.enabled = true;
    certification.inspectionDOI = inspectionDate;
    certification.minCertificationDate = inspectionDate;
    await updateDb('certifications', certification);
  }

  // Return updated data
  return {
    inspNumber: inspection.inspectionNumber,
    template: questions.template,
    inspection: {
      ...inspection,
      status,
      inspection_types,
      created_date: inspectionDate,
    },
    templateUnanswered: {
      status,
      unansweredAndRequired,
    },
  };
};

/**
 * Write a question's comment into the offline cache.
 *
 * `db.questions` is otherwise a download-time snapshot: the download writes it
 * (`InspectionsDue.jsx`) and the offline branch below writes it, and nothing
 * else did — so a comment added or removed *online* after the download left the
 * cache stale.
 *
 * That mattered beyond a wrong screen. The delete path decides whether the
 * server holds a comment by reading this cache, so a comment created online
 * post-download looked absent, a later offline delete queued nothing, and the
 * comment came back on the next sync — the very bug this ticket is fixing,
 * reached by a different route. (Found in review.)
 *
 * Deliberately narrow: only the one question's `comment`, not the whole template
 * the server returned. Overwriting the template wholesale would discard answers
 * that are sitting in the cache waiting to be replayed.
 */
const cacheQuestionComment = async (questionId, inspectionId, comment) => {
  const question = await db.questions.get(Number(inspectionId)).catch(() => null);

  // No cached record means the project was never downloaded — nothing to keep
  // in step, and nothing offline will read it.
  if (!question?.template?.question_groups) {
    return;
  }

  const question_groups = question.template.question_groups.map((group) => ({
    ...group,
    questions: (group.questions || []).map((q) =>
      q.id === Number(questionId) ? { ...q, comment: comment || '' } : q,
    ),
  }));

  // Swallowed, and deliberately not logged. By this point the comment is already
  // on the server, so there is nothing lost to report — the cost of a failure
  // here is a stale cached comment, which the next download corrects. Rejecting
  // would fail a write that succeeded; logging would put an error in the console
  // for an operation that worked.
  await db.questions
    .update(Number(inspectionId), {
      template: { ...question.template, question_groups },
    })
    .catch(() => undefined);
};

const createOfflineQuestionComment = async (questionId, inspectionId, data) => {
  const question = await db.questions.get(Number(inspectionId)).catch((e) => {
    console.log('error', e);
    throw e;
  });

  if (!question) {
    throw new Error('Question not found');
  }
  if (!question.template.question_groups) {
    throw new Error('Question groups not found');
  }

  const isDelete = Boolean(data.shouldDelete);
  const matchesQuestion = (payload) =>
    payload.question_id === Number(questionId);

  /**
   * Whether the server already holds a comment for this question.
   *
   * Read *before* `questionData` below, which assigns straight into `q` — the
   * objects inside `question_groups` are the cached ones, not copies. After that
   * line the cached comment is this edit, and there is no longer any way to tell
   * what the server had.
   */
  const commentOnServer = (question.template.question_groups || [])
    .flatMap((group) => group.questions || [])
    .find((q) => q.id === Number(questionId))?.comment;

  const queued = await db.commentPayload
    .where('inspection_id')
    .equals(Number(inspectionId))
    .and(matchesQuestion)
    .toArray();

  const unsynced = queued.filter((payload) => !payload.sync?.synced);

  /**
   * Carried forward from the queued write rather than re-read, for the reason
   * above: only the *first* offline write for a question can observe the
   * server's state, so it records it and later writes inherit it.
   *
   * Rows queued before this change have no flag. They fall back to the cached
   * comment, which by then is the local edit and so reads as "the server has
   * one" — deliberately, because for those the failure being fixed here is worse
   * than the empty row described below. New rows all carry the flag.
   */
  const carried = unsynced.find(
    (payload) => typeof payload.existed_on_server === 'boolean',
  );
  const existedOnServer = carried
    ? carried.existed_on_server
    : Boolean(commentOnServer);

  const questionData = question.template.question_groups.map((group) => {
    return {
      ...group,
      questions: group.questions.map((q) => {
        if (q.id === Number(questionId)) {
          q.comment = data.comment || '';
        }

        return q;
      }),
    };
  });

  /**
   * SW2-2321 — one queued write per question, replacing rather than appending.
   *
   * The endpoint upserts on (question, inspection, user) and the server holds at
   * most one comment per question, so the queue should carry the final intent
   * rather than a log of every keystroke. Appending meant three offline edits
   * replayed as three POSTs.
   *
   * The delete branch used to stop here, dropping the queue and writing nothing
   * replayable — so the server never heard about the deletion and the comment
   * came back on the next download. That is the QA report this fixes. The same
   * endpoint performs the delete: it takes `shouldDelete` and calls `destroy()`.
   */
  await db.commentPayload
    .where('inspection_id')
    .equals(Number(inspectionId))
    .and(matchesQuestion)
    .delete();

  if (!isDelete || existedOnServer) {
    await db.commentPayload.add({
      inspection_id: Number(inspectionId),
      question_id: Number(questionId),
      existed_on_server: existedOnServer,
      sync: {
        method: 'POST',
        // Only `shouldDelete` on the delete path. The endpoint ignores the text
        // when destroying, and carrying it would suggest the two disagree.
        payload: isDelete ? { shouldDelete: true } : data,
        synced: false,
      },
    });
  }
  // Otherwise the comment only ever existed in this browser: dropping the queued
  // write above is the whole job. Queueing a delete would be worse than doing
  // nothing — the endpoint's findOrCreate would insert a row with no comment and
  // then take its "newly created, do nothing" branch, leaving an empty one.

  await db.questions.update(Number(inspectionId), {
    template: {
      ...question.template,
      question_groups: questionData,
    },
  });

  const newQuestions = await db.questions
    .get(Number(inspectionId))
    .catch((e) => {
      console.log('error', e);
      throw e;
    });

  return newQuestions.template;
};

/**
 * Build an inspection locally, for when the server cannot be reached.
 *
 * Extracted from `create` so that a caller which must NOT touch the network —
 * `createWithJunction`'s offline path — can reach it directly. Going back
 * through `inspectionApi.create()` re-entered a network-capable wrapper: with
 * `navigator.onLine` still reporting true it issued a *second* POST, and that
 * retry omitted `junctionId`, so a connection that recovered between the two
 * attempts created the inspection against the primary form instead of the
 * selected junction. (Raised by Copilot on PR #53.)
 */
const createInspectionOffline = async (projectId, types, inspectionTypeId) => {
  const latestInspection = await db.inspections
    .where('project_id')
    .equals(Number(projectId))
    .reverse()
    .sortBy('inspectionNumber')
    .catch((e) => {
      throw e;
    });

  const project = await db.projects.get(Number(projectId));
  const user = await db.user.get(1);

  const projectInspectionUploads = await db.projectInspectionUploads
    .where('project_id')
    .equals(Number(projectId))
    .and((upload) => upload.inspection_id === latestInspection[0]?.id)
    .toArray();

  // Get the latest inspection's template to check inspection_type_id
  let latestInspectionTemplate = null;

  if (latestInspection.length > 0) {
    latestInspectionTemplate = await db.inspectionTemplate.get(
      Number(latestInspection[0].template_id),
    );
  } else {
    console.log('❌ No latest inspection found');
  }

  // `currentInspectionTemplate` was referenced here but never defined
  // anywhere in this module, so this threw a ReferenceError on every offline
  // create. Compare the requested inspection type against the latest
  // inspection's type directly instead. Note the stored template row is
  // { template_id, template, ... }, so the type lives under `.template`.
  const latestInspectionTypeId =
    latestInspectionTemplate?.template?.inspection_type_id ??
    latestInspectionTemplate?.inspection_type_id ??
    null;

  // Deliberately conservative: when the type cannot be determined we do NOT
  // carry findings over, since wrongly copying them into a new inspection is
  // worse than omitting them.
  const shouldCarryOverFindings =
    latestInspection.length > 0 &&
    inspectionTypeId != null &&
    latestInspectionTypeId != null &&
    Number(inspectionTypeId) === Number(latestInspectionTypeId);

  if (shouldCarryOverFindings) {
    const data = {
      inspectionNumber: latestInspection[0].inspectionNumber + 1,
      project_id: latestInspection[0].project_id,
      inspection_types: Array.isArray(types) ? types : [types],
      acknowledgement_date: null,
      created_at: new Date(),
      updated_at: new Date(),
      created_date: getDateOnly(new Date()),
      findings: latestInspection[0].findings.filter(
        (finding) => finding.date_completed === null,
      ),
      id: Number(latestInspection[0].id) + 100,
      inspector: latestInspection[0].inspector,
      inspection_template: latestInspection[0].inspection_template,
      project: latestInspection[0].project,
      status: 'new',
      template_id: latestInspection[0].template_id,
      updated_date: formatISO(new Date()),
      certification_date: null,
      compliance_date: null,
      certification_notes: null,
      sync: {
        method: 'POST', // POST, PUT, or DELETE
        payload: {
          projectId,
          inspection_types: Array.isArray(types) ? types : [types],
        },
        synced: false,
      },
    };

    const question = await db.questions
      .get(Number(latestInspection[0].id))
      .catch((e) => {
        console.log('error', e);
        throw e;
      });

    const certification = await db.certifications
      .get(Number(latestInspection[0].id))
      .catch((e) => {
        console.log('error', e);
        throw e;
      });

    Object.keys(certification.userSignatures).forEach((key) => {
      certification.userSignatures[key] = [];
      certification.groupOptions[key].enabled = false;
      if (certification.groupOptions[key].errors.length === 0) {
        certification.groupOptions[key].errors.push(errors[key]);
      }
    });

    //empty question answers and comments
    question.template.question_groups.forEach((group) => {
      group.questions.forEach((question) => {
        if (question.features.includes('carryOver')) {
          question.comment = '';
        } else if (
          question.features.includes('isInspectionDate', 'defaultToToday')
        ) {
          question.answer = getDateOnly(new Date());
          question.comment = '';
        } else if (question.features.includes('defaultToProject')) {
          question.answer = project.name;
          question.comment = '';
        } else if (question.features.includes('defaultToCurrentUser')) {
          question.answer = user.name;
          question.comment = '';
        } else {
          question.answer = '';
          question.comment = '';
        }
      });
    });

    await db.certifications
      .add({ ...certification, inspectionId: data.id })
      .catch((e) => {});

    await db.questions
      .add({ ...question, inspection_id: data.id })
      .catch((e) => {
        console.log('error', e);
      });

    await db.inspectionNotes
      .add({
        inspection_id: data.id,
        notes: [],
        sync: { synced: true, payload: [] },
      })
      .catch((e) => {
        console.log('error', e);
      });

    if (projectInspectionUploads.length > 0) {
      await db.projectInspectionUploads
        .add({
          project_id: Number(projectId),
          inspection_id: data.id,
          data: projectInspectionUploads[0].data.map((uploads) => {
            return {
              ...uploads,
              inspection_id: data.id,
            };
          }),
        })
        .catch((e) => {
          console.log('error', e);
        });
    }

    await db.projects
      .update(projectId, {
        inspections: [
          ...project.inspections,
          {
            created_date: getDateOnly(new Date()),
            id: data.id,
            inspection_types: data.inspection_types,
          },
        ],
      })
      .catch((e) => {
        console.log('error', e);
        throw e;
      });

    const inspection = await db.inspections.add(data).catch((e) => {
      throw e;
    });

    return db.inspections.get(inspection).catch((e) => {
      throw e;
    });
  } else {
    const inspectionTemplate = await db.inspectionTemplate.get(
      Number(project.inspection_template_id),
    );

    if (!inspectionTemplate) {
      return Promise.reject(
        new Error('Inspection template not found for this project'),
      );
    }
    const data = {
      inspectionNumber: latestInspection.length
        ? latestInspection[0].inspectionNumber + 1
        : 1,
      project_id: projectId,
      inspection_types: Array.isArray(types) ? types : [types],
      acknowledgement_date: null,
      created_at: new Date(),
      updated_at: new Date(),
      created_date: getDateOnly(new Date()),
      findings: [],
      id: latestInspection.length
        ? Number(latestInspection[0].id) + 100
        : random(false, 1000000),
      inspector: [],
      inspection_template: {
        has_acknowledgement: inspectionTemplate.template.has_acknowledgement,
        has_compliance_cert: inspectionTemplate.template.has_compliance_cert,
      },
      project: latestInspection.length
        ? latestInspection[0].project
        : { id: projectId, client: { disable_carry_overs: false } },
      status: 'new',
      template_id: project.inspection_template_id,
      updated_date: formatISO(new Date()),
      certification_date: null,
      compliance_date: null,
      certification_notes: null,
      sync: {
        method: 'POST', // POST, PUT, or DELETE
        payload: {
          projectId,
          inspection_types: Array.isArray(types) ? types : [types],
        },
        synced: false,
      },
    };

    const question = inspectionTemplate;
    const certifications = inspectionTemplate.signature;

    certifications.userSignatures = certifications.userSignatures || {};
    certifications.groupOptions = certifications.groupOptions || {};

    Object.keys(certifications.attestations).forEach((key) => {
      certifications.userSignatures[key] = [];

      certifications.groupOptions[key] = certifications.groupOptions[key] || {
        enabled: false,
        errors: [],
      };

      if (certifications.groupOptions[key].errors.length === 0) {
        certifications.groupOptions[key].errors.push(errors[key]);
      }
    });

    //empty question answers and comments
    question.template.question_groups.forEach((group) => {
      group.questions.forEach((question) => {
        if (question.features.includes('carryOver')) {
          question.comment = '';
          question.answer = '';
        } else if (
          question.features.includes('isInspectionDate', 'defaultToToday')
        ) {
          question.answer = getDateOnly(new Date());
          question.comment = '';
        } else if (question.features.includes('defaultToProject')) {
          question.answer = project.name;
          question.comment = '';
        } else if (question.features.includes('defaultToCurrentUser')) {
          question.answer = user.name;
          question.comment = '';
        } else {
          question.answer = '';
          question.comment = '';
        }
      });
    });

    await db.certifications
      .add({
        ...certifications,
        inspectionId: data.id,
        sync: { synced: true, payload: [] },
      })
      .catch((e) => {
        throw e;
      });

    await db.questions
      .add({
        inspNumber: data.inspectionNumber,
        template: question.template,
        inspection_id: data.id,
        questionTypes: question.questionTypes,
      })
      .catch((e) => {
        throw e;
      });

    await db.inspectionNotes
      .add({
        inspection_id: data.id,
        notes: [],
        sync: { synced: true, payload: [] },
      })
      .catch((e) => {
        console.log('error', e);
        throw e;
      });

    if (projectInspectionUploads.length > 0) {
      await db.projectInspectionUploads
        .add({
          project_id: Number(projectId),
          inspection_id: data.id,
          data: projectInspectionUploads[0].data.map((uploads) => {
            return {
              ...uploads,
              inspection_id: data.id,
            };
          }),
        })
        .catch((e) => {
          console.log('error', e);
        });
    }

    await db.projects
      .update(projectId, {
        inspections: [
          ...project.inspections,
          {
            created_date: getDateOnly(new Date()),
            id: data.id,
            inspection_types: data.inspection_types,
          },
        ],
      })
      .catch((e) => {
        console.log('error', e);
        throw e;
      });

    const inspection = await db.inspections.add(data).catch((e) => {
      throw e;
    });

    return db.inspections.get(inspection).catch((e) => {
      throw e;
    });
  }
};

export const inspectionApi = {
  get: async (id) => {
    const fromCache = async () => {
      const data = await db.inspections.get(Number(id));

      // Not downloaded — let the caller fall through rather than throwing a
      // TypeError on data.findings below.
      if (!data) {
        return null;
      }

      const findings = await db.findings
        .where('id')
        .anyOf((data.findings || []).map((f) => f.id))
        .toArray();

      data.findings = findings?.length ? findings : data.findings;

      return { inspection: data };
    };

    return withOfflineFallback(
      () => xhrService.get(getUrl(id)).then((res) => res.data),
      fromCache,
    );
  },
  /**
   * NOTE ON `inspectionTypeId`: every current caller passes a source *label*
   * rather than an id — 'sync offline', 'External Project View',
   * 'precipitation widget', 'old admin view'. The finding carry-over logic in the
   * offline branch below compares it numerically, so `Number(label)` is NaN and
   * carry-over can never fire. Whether it should is an open question — see
   * SW2-2224-frontend-review-response.md (F6). Do not assume it works.
   */
  create: (projectId, types, inspectionTypeId = null, opts = {}) =>
    // SW2-2321: was a `navigator.onLine` pre-check. After an offline reload the
    // POST was attempted, failed, and no local inspection row was written — the
    // inspector tapped "start inspection" and got nothing at all.
    //
    // `opts.replay` matters here too: the sync loop calls this to flush an
    // inspection that was created offline. Queueing there would build a *second*
    // local inspection and resolve with it, and the caller would then stamp the
    // original row with that local id and mark it synced — so the real
    // inspection never reaches the server. (Raised by Copilot on PR #53.)
    withOfflineWrite(
      async () => {
        const requestBody = {
          projectId,
          inspection_types: Array.isArray(types) ? types : [types],
        };

        // Only forwarded when it is actually an id. Previously any truthy value was
        // sent, so the server received `inspection_type_id: 'sync offline'`. Both
        // produce no carry-over, but sending a label to the API is worse than
        // omitting the field.
        const numericTypeId = Number(inspectionTypeId);

        if (inspectionTypeId != null && Number.isInteger(numericTypeId)) {
          requestBody.inspection_type_id = numericTypeId;
        }

        return xhrService
          .post(baseUrl, requestBody)
          .then((res) => {
            return res.data;
          })
          .catch((e) => {
            throw e;
          });
      },
      () => createInspectionOffline(projectId, types, inspectionTypeId),
      opts,
    ),
  createWithJunction: (requestBody, opts = {}) =>
    // SW2-2321: was a `navigator.onLine` pre-check. After an offline reload the
    // POST was attempted, failed, and no local inspection was created — the
    // inspector was left with nothing to work in.
    withOfflineWrite(
      () => xhrService.post(baseUrl, requestBody).then((res) => res.data),
      // Straight to the local builder, never back through create(): that is a
      // network-capable wrapper and would fire a second POST without
      // `junctionId`. (Raised by Copilot on PR #53.)
      () =>
        createInspectionOffline(
          requestBody.projectId,
          requestBody.inspection_types,
        ),
      opts,
    ),
  update: (id, data, opts = {}) =>
    // SW2-2321: was a `navigator.onLine` pre-check. After an offline reload the
    // PUT was attempted, failed, and the edit was never queued for sync.
    withOfflineWrite(
      async () => {
        const updatedData = await xhrService
          .put(getUrl(id), data)
          .then((res) => res.data);

        // SW2-2321: skipped on replay; the answers are already in IndexedDB and
        // the sync loop marks the row itself.
        if (!opts.replay) {
          await updateOfflineInspection(id, data, true).catch((error) => {
            console.log('Error updating offline inspection', error);
          });
        }

        return updatedData;
      },
      () => updateOfflineInspection(id, data, false),
      opts,
    ),
  destroy: (id, reason) =>
    xhrService.delete(getUrl(id), { data: { reason } }).then((res) => res.data),
  complete: (id, findings, dateCompleted, comments = undefined) => {
    const reqBody = {
      findings,
      dateCompleted,
    };

    if (comments) {
      reqBody.comments = comments;
    }

    return xhrService
      .put(`${baseUrl}/${id}/findings`, reqBody)
      .then((res) => res.data);
  },
  createQuestionComment: (questionId, inspectionId, data, opts = {}) =>
    // SW2-2321: was a `navigator.onLine` pre-check. After an offline reload the
    // POST was attempted, failed, and the comment was silently lost.
    withOfflineWrite(
      async () => {
        const result = await xhrService
          .post(`${baseUrl}/comment/${questionId}/${inspectionId}`, data)
          .then((res) => res.data);

        // Skipped on replay: the cache already holds this value — replaying it
        // is how it got to the server — and the queued row is about to be
        // marked synced.
        if (!opts.replay) {
          await cacheQuestionComment(
            questionId,
            inspectionId,
            data.shouldDelete ? '' : data.comment,
          );
        }

        return result;
      },
      () => createOfflineQuestionComment(questionId, inspectionId, data),
      opts,
    ),
  reassign: (inspectionId, userId, projectId, reassignMode = 'primary') => {
    return xhrService
      .post(`${baseUrl}/${inspectionId}/inspector/${userId}`, {
        projectId: projectId,
        reassignMode: reassignMode, // "primary" or "next_only"
      })
      .then((res) => res.data);
  },
  reassignV2: (userId, projectIds, clientIds, reassignMode = 'primary') => {
    return xhrService
      .post(`${baseUrl}/reassign/${userId}`, {
        projectIds: projectIds,
        clientIds: clientIds,
        reassignMode: reassignMode, // "primary" or "next_only"
      })
      .then((res) => res.data);
  },
  getInspectionLinkedAllData: (id) => {
    return xhrService
      .get(`/api/inspection/${id}`)
      .then((res) => res.data)
      .catch((error) => console.log(error));
  },

  getInspectionMonthWise: (projectId, date_range) => {
    return xhrService
      .get(
        `/api/inspections/getInspectionMonthWise/${projectId}?date_range[]=${date_range[0]}&date_range[]=${date_range[1]}`,
      )
      .then((res) => res.data)
      .catch((error) => console.log(error));
  },
  getInspectionUploads: (id) =>
    // SW2-2321: was a `navigator.onLine` pre-check. After an offline reload the
    // request was attempted and the cached uploads were never consulted.
    //
    // The online branch used to end in `.catch((error) => console.log(error))`,
    // which resolved to `undefined` on any failure. That has to go: the helper
    // can only reach the cache if the request actually rejects.
    withOfflineFallback(
      () => xhrService.get(`${baseUrl}/${id}/uploads`).then((res) => res.data),
      async () => {
        const res = await db.inspectionUploads.get(id);

        // null (not []) on a miss so the helper surfaces the request error
        // rather than presenting "no uploads" as a successful empty list.
        return res ? res.uploads : null;
      },
    ),
  sortInspUploads: (id, order) => {
    console.log(order);

    return xhrService
      .put(`${baseUrl}/${id}/sortInspUploads`, {
        order: order,
      })
      .then((res) => res.data)
      .catch((error) => console.log(error));
  },
  cancelOverdue: (data) => {
    return xhrService
      .post(`${baseUrl}/cancel-overdue`, data)
      .then((res) => res.data)
      .catch((error) => {
        console.log(error);
        throw error;
      });
  },
  getTenantBackdatingDetails: () => {
    return xhrService
      .get(`${baseUrl}/getTenantBackdatingDetails`)
      .then((res) => res.data)
      .catch((error) => {
        console.log(error);
        throw error;
      });
  },
};

export default inspectionApi;
